"use client";

import { useEffect } from "react";

/**
 * Bloquea el zoom no deseado en dispositivos móviles.
 *
 * El meta viewport (`user-scalable=no, maximum-scale=1`) basta en Android,
 * pero iOS Safari lo ignora desde iOS 10+. Aquí bloqueamos explícitamente:
 * - Pinch-zoom (eventos `gesture*` no estándar de Safari + fallback multi-touch)
 * - Double-tap zoom
 *
 * El scroll normal de un dedo se preserva: solo cancelamos `touchmove`
 * cuando hay más de un dedo en pantalla (gesto de zoom).
 *
 * Nota: el zoom automático al enfocar un input se resuelve por CSS, no aquí:
 * los controles llevan 16px lógicos (lo que mira iOS) y se reducen visualmente
 * con transform: scale(). Ver el bloque final de styles/globals.css.
 */
export function PreventZoom() {
  useEffect(() => {
    // Pinch-zoom en Safari/iOS (eventos "gesture*")
    const preventGesture = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", preventGesture);
    document.addEventListener("gesturechange", preventGesture);
    document.addEventListener("gestureend", preventGesture);

    // Double-tap zoom
    let lastTouchEnd = 0;
    const preventDoubleTap = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };
    document.addEventListener("touchend", preventDoubleTap, { passive: false });

    // Pinch multi-touch (fallback para navegadores sin eventos "gesture*")
    const preventMultiTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };
    document.addEventListener("touchmove", preventMultiTouch, {
      passive: false,
    });

    return () => {
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchend", preventDoubleTap);
      document.removeEventListener("touchmove", preventMultiTouch);
    };
  }, []);

  return null;
}
